import { RefObject, useEffect, useState } from "react"
import { HUBSPOT_FORMS_SCRIPT_SRC } from "../utils/hubspot"

export type HubspotFormStatus = 'loading' | 'ready' | 'error'

const FORM_RENDER_TIMEOUT_MS = 10000

/**
 * HubSpot の埋め込みフォームをマウントするフック。
 *
 * 埋め込みローダーは DOM 上の div.hs-form-frame を走査してフォームを描画するため、
 * 「コンテナが DOM にある状態で script を挿入する」順序が必要になる。
 * 後から挿入された frame をローダーが検知してくれるかに依存しないよう、
 * マウントごとに script 要素を作り直し、アンマウント時に取り除く。
 *
 * @param containerRef div.hs-form-frame の ref (フォームのマウント先)
*/
export const useHubspotForm = (
  containerRef: RefObject<HTMLDivElement>,
): { status: HubspotFormStatus } => {
  const [status, setStatus] = useState<HubspotFormStatus>('loading')

  useEffect(() => {
    const container = containerRef.current
    if (!container) {
      setStatus('error')
      return
    }

    let settled = false
    const settle = (next: HubspotFormStatus) => {
      if (settled) return
      settled = true
      setStatus(next)
    }

    const observer = new MutationObserver(() => {
      if (container.childElementCount > 0) {
        settled = true
        setStatus('ready')
      }
    })
    observer.observe(container, { childList: true, subtree: true })

    const timeoutId = window.setTimeout(() => settle('error'), FORM_RENDER_TIMEOUT_MS)

    const script = document.createElement('script')
    script.src = HUBSPOT_FORMS_SCRIPT_SRC
    script.async = true
    script.onerror = () => settle('error')
    document.head.appendChild(script)

    return () => {
      settled = true
      observer.disconnect()
      window.clearTimeout(timeoutId)
      script.remove()
      container.innerHTML = ''
    }
  }, [containerRef])

  return { status }
}
